import {useItemPickersContext} from '@github-ui/item-picker'
import PreloadedQueryBoundary from '@github-ui/relay-preloaded-query-boundary'
import {Box, Heading, IconButton, Overlay} from '@primer/react'
import {Suspense, useEffect, useRef, useCallback, useState, useMemo} from 'react'
import {type PreloadedQuery, useQueryLoader, graphql, usePreloadedQuery} from 'react-relay'
import {useFragment, useRelayEnvironment} from 'react-relay/hooks'
import {useFeatureFlags} from '@github-ui/react-core/use-feature-flag'
import {ssrSafeLocation} from '@github-ui/ssr-utils'
import {useContainerBreakpoint} from '@github-ui/use-container-breakpoint'
import {noop} from '@github-ui/noop'
import type {IssueCommentComposerRef} from '@github-ui/commenting'
import {IssueCommentComposerInternal, useIssueCommentEditsContext} from '@github-ui/commenting'
import {IssueBodyInternal} from '@github-ui/issue-body'

import {CLASS_NAMES, IDS, TEST_IDS} from '../constants'
import {useInputElementActiveContext, useIssueViewerIssueEditContext} from '../contexts'
import type {ItemIdentifier} from '../types/issue'
import {Header} from './header/Header'
import {IssueTimelineInternal} from './IssueTimeline'
import {ScrollToTop} from './ScrollToTop'
import {MetadataSectionInternal} from './sections/MetadataSection'
import {ProjectSectionInternal} from './sections/ProjectsSection'
import {RelationsSectionInternal} from './sections/RelationsSection'
import {renderIssueViewerErrors} from './shared/IssueViewerError'
import FeedbackLink from './shared/FeedbackLink'
import type {IssueViewerViewQuery} from './__generated__/IssueViewerViewQuery.graphql'
import {IssueViewerLoading} from './IssueViewerLoading'
import type {IssueViewerViewer$key} from './__generated__/IssueViewerViewer.graphql'
import type {IssueViewerView$key} from './__generated__/IssueViewerView.graphql'
import {ContentWrapper} from './ContentWrapper'
import {MilestonesSectionInternal} from './sections/MilestonesSection'
import {getHightlightedEventText} from '../utils/urls'
import {ParticipantsSection} from './sections/ParticipantsSection'
import {OldExperienceReloadBanner} from './OldExperienceReloadBanner'
import {DevelopmentSectionInternal} from './sections/development-section/DevelopmentSection'
import {InfoIcon, XIcon} from '@primer/octicons-react'
import {ISSUE_VIEWER_DEFAULT_CONFIG, type OptionConfig} from './OptionConfig'
import {markNotificationAsReadIssueViewerMutation} from '../mutations'
import {LABELS} from '@github-ui/timeline-items'
import {ReadonlySectionHeader, Section} from '..'

export type IssueViewerQueries = {
  issueViewerViewQuery: IssueViewerViewQuery
}

type IssueViewerProps = {
  itemIdentifier: ItemIdentifier
  optionConfig?: OptionConfig
}

type IssueViewerInternalProps = {
  optionConfig: OptionConfig
  issueViewerViewRef: PreloadedQuery<IssueViewerViewQuery>
  containerRef: React.RefObject<HTMLDivElement>
}

type IssueViewerInternalFragmentProps = {
  optionConfig: OptionConfig
  viewerFragment: IssueViewerViewer$key
  issueFragment: IssueViewerView$key
  containerRef: React.RefObject<HTMLDivElement>
}

export const IssueViewerViewGraphqlQuery = graphql`
  query IssueViewerViewQuery($repo: String!, $owner: String!, $number: Int!, $allowedOwner: String) {
    repository(name: $repo, owner: $owner) {
      issue(number: $number) @required(action: THROW) {
        ...IssueViewerView @arguments(allowedOwner: $allowedOwner)
      }
    }
    viewer {
      ...IssueViewerViewer
    }
  }
`

const issueViewerViewFragment = graphql`
  fragment IssueViewerView on Issue @argumentDefinitions(allowedOwner: {type: "String", defaultValue: null}) {
    id
    # this is not directly requried for rendering, but is used to determine if the issue is read by the viewer
    # and to mark the issue as read if we don't fetch this field we have to make an additional request to mark the issue as read
    isReadByViewer
    ...Header
    ...IssueBody
    ...IssueCommentComposer
    ...IssueActions
    ...MetadataSectionFragment
    ...MilestonesSectionFragment
    ...ProjectsSectionFragment @arguments(allowedOwner: $allowedOwner)
    ...RelationsSectionFragment
    ...DevelopmentSectionFragment
    ...IssueTimelinePaginated @arguments(numberOfTimelineItems: 15)
    ...ParticipantsSectionFragment
    issueType {
      ...IssueTypesDialogIssueType
    }
  }
`

const issueViewerViewerFragment = graphql`
  fragment IssueViewerViewer on User {
    ...ReactionViewerUser
    ...AssigneePickerAssignee
  }
`

export function IssueViewer({itemIdentifier, optionConfig = ISSUE_VIEWER_DEFAULT_CONFIG}: IssueViewerProps) {
  const {repo, owner, number} = itemIdentifier

  const [issueViewerViewRef, loadIssueViewerView] = useQueryLoader<IssueViewerViewQuery>(
    IssueViewerViewGraphqlQuery,
    optionConfig.preloadedQueries?.issueViewerViewQuery,
  )
  const issueViewerContainerRef = useRef<HTMLDivElement>(null)

  const fetchPolicy = optionConfig.issueQueriesFetchingPolicy?.fetchPolicy
  useEffect(() => {
    if (!optionConfig.preloadedQueries?.issueViewerViewQuery) {
      loadIssueViewerView({owner, repo, number, allowedOwner: optionConfig.allowedProjectOwner ?? null}, {fetchPolicy})
    }
  }, [
    loadIssueViewerView,
    owner,
    repo,
    optionConfig.preloadedQueries?.issueViewerViewQuery,
    optionConfig.allowedProjectOwner,
    fetchPolicy,
    number,
  ])

  if (!issueViewerViewRef) return <IssueViewerLoading />

  return (
    <PreloadedQueryBoundary
      key={`${owner}-${repo}-${number}`}
      onRetry={() =>
        loadIssueViewerView(
          {owner, repo, number, allowedOwner: optionConfig.allowedProjectOwner ?? null},
          {fetchPolicy: 'network-only'},
        )
      }
      fallback={renderIssueViewerErrors}
    >
      <Box
        ref={issueViewerContainerRef}
        sx={{
          display: 'flex',
          flex: 'auto',
          flexDirection: 'column',
          justifyContent: 'stretch',
          alignItems: 'center',
          position: 'relative',
          pt: 3,
        }}
      >
        <Suspense fallback={<IssueViewerLoading />}>
          <IssueViewerInternal
            issueViewerViewRef={issueViewerViewRef}
            containerRef={issueViewerContainerRef}
            optionConfig={optionConfig}
          />
        </Suspense>
      </Box>
    </PreloadedQueryBoundary>
  )
}

function IssueViewerInternal({issueViewerViewRef, containerRef, optionConfig}: IssueViewerInternalProps) {
  const {repository, viewer} = usePreloadedQuery<IssueViewerViewQuery>(IssueViewerViewGraphqlQuery, issueViewerViewRef)

  return viewer && repository?.issue ? (
    <IssueViewerInternalFragment
      viewerFragment={viewer}
      issueFragment={repository.issue}
      containerRef={containerRef}
      optionConfig={optionConfig}
    />
  ) : null
}

export function IssueViewerInternalFragment({
  viewerFragment,
  issueFragment,
  optionConfig,
  containerRef,
}: IssueViewerInternalFragmentProps) {
  const issue = useFragment(issueViewerViewFragment, issueFragment)
  const viewer = useFragment(issueViewerViewerFragment, viewerFragment)
  const environment = useRelayEnvironment()

  const composerRef = useRef<IssueCommentComposerRef>(null)

  const {startCommentEdit, cancelCommentEdit, clearCommentEditStates} = useIssueCommentEditsContext()
  const {endIssueEdit, startIssueEdit, isIssueEditActive} = useIssueViewerIssueEditContext()
  const {setInputElementState, clearInputElementStates} = useInputElementActiveContext()
  const {anyItemPickerOpen} = useItemPickersContext()

  const breakpoint = useContainerBreakpoint(containerRef.current)

  // Reset comment and input states on issue change
  useEffect(() => {
    clearCommentEditStates()
    clearInputElementStates()
  }, [issue.id, clearCommentEditStates, clearInputElementStates])

  useEffect(() => {
    if (!issue.isReadByViewer) {
      markNotificationAsReadIssueViewerMutation({environment, notificationId: issue.id})
    }
  }, [environment, issue.id, issue.isReadByViewer])

  useEffect(() => {
    setInputElementState(IDS.itemPicker, anyItemPickerOpen())
  }, [anyItemPickerOpen, setInputElementState])

  // Whenever the issue body editing state changes, synchronize that state via callbacks
  const onIssueEditStateChange = optionConfig.onIssueEditStateChange
  useEffect(() => {
    onIssueEditStateChange?.(isIssueEditActive)
  }, [isIssueEditActive, onIssueEditStateChange])

  const handleCommentReply = useCallback((quotedComment: string) => {
    composerRef.current?.setText(quotedComment)
    setTimeout(() => composerRef.current?.focus(), 0)
  }, [])

  const {tasklist_block} = useFeatureFlags()
  const onCommentChange = useCallback(
    (id: string) => {
      startCommentEdit(id)
      optionConfig.onCommentEditStart?.(id)
    },
    [optionConfig, startCommentEdit],
  )
  const onCommentEditCancel = useCallback(
    (id: string) => {
      cancelCommentEdit(id)
      optionConfig.onCommentEditCancel?.(id)
    },
    [cancelCommentEdit, optionConfig],
  )

  const [detailPaneOpen, setDetailPaneOpen] = useState(false)
  const exitOverlay = useCallback(() => setDetailPaneOpen(false), [])

  const isNarrow = useMemo(() => breakpoint([true, true, false, false]), [breakpoint])

  const MetadataContent = useCallback(
    () => (
      <>
        <MetadataSectionInternal
          issue={issue}
          viewer={viewer}
          onIssueUpdate={optionConfig.onIssueUpdate}
          singleKeyShortcutsEnabled={optionConfig.singleKeyShortcutsEnabled || false}
          prefetchInteractionDataEnabled={optionConfig.prefetchInteractionDataEnabled || false}
        />
        <MilestonesSectionInternal
          issue={issue}
          onIssueUpdate={optionConfig.onIssueUpdate}
          singleKeyShortcutsEnabled={optionConfig.singleKeyShortcutsEnabled || false}
          prefetchInteractionDataEnabled={optionConfig.prefetchInteractionDataEnabled || false}
        />
        {tasklist_block && <RelationsSectionInternal issue={issue} onLinkClick={optionConfig.onLinkClick} />}
        <DevelopmentSectionInternal
          issue={issue}
          onIssueUpdate={optionConfig.onIssueUpdate}
          prefetchInteractionDataEnabled={optionConfig.prefetchInteractionDataEnabled || false}
          singleKeyShortcutsEnabled={optionConfig.singleKeyShortcutsEnabled || false}
        />
        <PreloadedQueryBoundary
          onRetry={noop}
          fallback={() => {
            return (
              <Section
                sectionHeader={<ReadonlySectionHeader title={'Projects are currently unavailable'} />}
                emptyText={'Please try again later'}
              >
                <></>
              </Section>
            )
          }}
        >
          <ProjectSectionInternal
            selectedProjectId={optionConfig.selectedProjectId}
            allowedProjectOwner={optionConfig.allowedProjectOwner}
            issueOrPullRequest={issue}
            onIssueUpdate={optionConfig.onIssueUpdate}
            singleKeyShortcutsEnabled={optionConfig.singleKeyShortcutsEnabled || false}
            prefetchInteractionDataEnabled={optionConfig.prefetchInteractionDataEnabled || false}
          />
        </PreloadedQueryBoundary>
        <ParticipantsSection issue={issue} />
        {optionConfig.feedbackLink && <FeedbackLink title="Alpha" feedbackUrl={optionConfig.feedbackLink} />}
        {optionConfig.showReloadInOldExperience && <OldExperienceReloadBanner />}
      </>
    ),
    [
      issue,
      optionConfig.allowedProjectOwner,
      optionConfig.feedbackLink,
      optionConfig.onIssueUpdate,
      optionConfig.onLinkClick,
      optionConfig.prefetchInteractionDataEnabled,
      optionConfig.selectedProjectId,
      optionConfig.showReloadInOldExperience,
      optionConfig.singleKeyShortcutsEnabled,
      tasklist_block,
      viewer,
    ],
  )

  const MetadataPaneTrigger = useCallback(
    () => (
      <>
        {isNarrow && optionConfig.responsiveRightSidepanel && (
          <IconButton
            aria-label="Show metadata pane"
            icon={InfoIcon}
            variant="invisible"
            onClick={() => setDetailPaneOpen(true)}
            ref={toggleSidesheetRef}
          />
        )}
      </>
    ),
    [isNarrow, optionConfig.responsiveRightSidepanel],
  )

  const closeButtonRef = useRef<HTMLButtonElement>(null)
  const toggleSidesheetRef = useRef<HTMLButtonElement>(null)
  const renderRightOverlay = useMemo(
    () => optionConfig.responsiveRightSidepanel && isNarrow && detailPaneOpen,
    [detailPaneOpen, isNarrow, optionConfig.responsiveRightSidepanel],
  )
  const renderMetadataPane = useMemo(
    () => !isNarrow || (isNarrow && !optionConfig.responsiveRightSidepanel),
    [isNarrow, optionConfig.responsiveRightSidepanel],
  )
  return (
    <>
      <Header
        issue={issue}
        optionConfig={optionConfig}
        navigate={optionConfig.navigate ?? noop}
        navigateBack={optionConfig.navigateBack}
        navigatePrevious={optionConfig.navigatePrevious}
        navigateNext={optionConfig.navigateNext}
        additionalActions={optionConfig.additionalHeaderActions}
        customEditMenuEntries={optionConfig.customEditMenuEntries}
        showCreateIssueButton={optionConfig.showIssueCreateButton}
        onIssueDelete={optionConfig.onIssueDelete}
        onClose={optionConfig.onClose}
        markdownEditorConfig={optionConfig.markdownEditorConfig}
        singleKeyShortcutsEnabled={optionConfig.singleKeyShortcutsEnabled || false}
        issueId={issue.id}
        metadataPaneTrigger={<MetadataPaneTrigger />}
        prefetchInteractionDataEnabled={optionConfig.prefetchInteractionDataEnabled || false}
        collapsed={optionConfig.collapsed}
      />

      <ContentWrapper sx={optionConfig?.innerSx}>
        <Box
          sx={{
            display: 'flex',
            flex: 'auto',
            flexDirection: breakpoint(['column', 'column', 'row', 'row']),
            justifyContent: 'stretch',
            gap: [2, 2, 2, 4],
          }}
        >
          <Box
            sx={{
              width: breakpoint(['100%', '100%', '67%', '67%']),
              backgroundColor: 'canvas.default',
              zIndex: 1,
            }}
          >
            <Box data-testid={TEST_IDS.issueViewerIssueContainer}>
              <IssueBodyInternal
                isIssueEditActive={isIssueEditActive}
                endIssueEdit={endIssueEdit}
                issue={issue}
                viewer={viewer}
                onLinkClick={optionConfig.onLinkClick}
                markdownEditorConfig={optionConfig.markdownEditorConfig}
                onIssueUpdate={optionConfig.onIssueUpdate}
                startIssueEdit={startIssueEdit}
              />
            </Box>
            <Box data-testid={TEST_IDS.issueViewerCommentsContainer} className={CLASS_NAMES.commentsContainer}>
              <Box
                sx={{
                  mb: 3,
                }}
              >
                <ScrollToTop />
                <IssueTimelineInternal
                  issue={issue}
                  highlightedEventText={getHightlightedEventText(ssrSafeLocation.hash)}
                  viewer={viewer}
                  onLinkClick={optionConfig.onLinkClick}
                  onCommentReply={handleCommentReply}
                  onCommentChange={onCommentChange}
                  onCommentEditCancel={onCommentEditCancel}
                  markdownEditorConfig={optionConfig.markdownEditorConfig}
                  timelineEventBaseUrl={optionConfig.timelineEventBaseUrl}
                  withLiveUpdates={optionConfig.withLiveUpdates}
                  withSecondaryFetch={optionConfig.withSecondaryFetch}
                  navigate={optionConfig.navigate ?? noop}
                />
                <IssueCommentComposerInternal
                  ref={composerRef}
                  issue={issue}
                  onChange={() => {
                    startCommentEdit(IDS.newComment)
                    optionConfig.onCommentEditStart?.(IDS.newComment)
                  }}
                  onSave={() => {
                    cancelCommentEdit(IDS.newComment)
                    optionConfig.onCommentEditCancel?.(IDS.newComment)
                  }}
                  onCancel={() => {
                    cancelCommentEdit(IDS.newComment)
                    optionConfig.onCommentEditCancel?.(IDS.newComment)
                  }}
                  onNewIssueComment={optionConfig.onNewIssueComment}
                  markdownEditorConfig={optionConfig.markdownEditorConfig}
                  singleKeyShortcutEnabled={optionConfig.singleKeyShortcutsEnabled || false}
                />
              </Box>
            </Box>
          </Box>

          <Box
            sx={{
              width: breakpoint(['100%', '100%', '33%', '33%']),
            }}
          >
            {renderRightOverlay ? (
              <Overlay
                anchorSide="inside-left"
                aria-label="Details"
                initialFocusRef={closeButtonRef}
                position="fixed"
                returnFocusRef={toggleSidesheetRef}
                right={0}
                top={0}
                sx={{
                  p: 3,
                  width: '440px',
                  height: '100vh',
                  maxWidth: '100vw',
                  maxHeight: '100vh',
                  borderTopRightRadius: 0,
                  borderBottomRightRadius: 0,
                }}
                onClickOutside={exitOverlay}
                onEscape={exitOverlay}
              >
                <Box sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 3, mb: 2}}>
                  <Heading as="h2" sx={{fontSize: 2, fontWeight: 600}}>
                    {LABELS.metadataHeader}
                  </Heading>
                  <IconButton
                    ref={closeButtonRef}
                    aria-label="Close overview"
                    icon={XIcon}
                    variant="invisible"
                    onClick={exitOverlay}
                  />
                </Box>
                <MetadataContent />
              </Overlay>
            ) : (
              <> </>
            )}
            {renderMetadataPane && (
              <>
                <Heading as="h2" className="sr-only" sx={{fontSize: 2, ml: 3, mb: 2}}>
                  {LABELS.metadataHeader}
                </Heading>
                <MetadataContent />
              </>
            )}
          </Box>
        </Box>
      </ContentWrapper>
    </>
  )
}

try{ IssueViewer.displayName ||= 'IssueViewer' } catch {}
try{ IssueViewerInternal.displayName ||= 'IssueViewerInternal' } catch {}
try{ IssueViewerInternalFragment.displayName ||= 'IssueViewerInternalFragment' } catch {}
try{ MetadataContent.displayName ||= 'MetadataContent' } catch {}
try{ MetadataPaneTrigger.displayName ||= 'MetadataPaneTrigger' } catch {}